import { Project, PaginationMeta } from "@/types";
import ProjectItem from "./ProjectItem";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";

interface ProjectListProps {
  projects: Project[];
  isLoading: boolean;
  meta?: PaginationMeta;
  onPageChange: (page: number) => void;
  currentPage: number;
}

const ProjectList = ({
  projects,
  isLoading,
  meta,
  onPageChange,
  currentPage,
}: ProjectListProps) => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const page = searchParams.get("page");
    const restoredFilters: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      restoredFilters[key] = value;
    });
    if (page) {
      onPageChange(Number(page));
    }
  }, [searchParams, onPageChange]);

  if (isLoading) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">{t("projects.loading")}</p>
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
        <h3 className="text-xl font-medium text-gray-900 mb-2">
          {t("projects.noProjects")}
        </h3>
        <p className="text-gray-500">
          {t("projects.noProjectsDescription") || t("common.noData")}
        </p>
      </div>
    );
  }

  // Generate page number links
  const generatePageNumbers = () => {
    if (!meta) return [];

    const totalPages = meta.pages;
    const pageNumbers = [];

    // Always show first page
    pageNumbers.push(1);

    if (currentPage > 3) {
      pageNumbers.push("...");
    }

    // Show pages around current page
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      pageNumbers.push(i);
    }

    if (currentPage < totalPages - 2) {
      pageNumbers.push("...");
    }

    // Always show last page
    if (totalPages > 1) {
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {projects.map((project, index) => (
          <ProjectItem key={project.id} project={project} index={index} />
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        {meta && meta.pages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage > 1) onPageChange(currentPage - 1);
                  }}
                  className={
                    currentPage === 1
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>

              {generatePageNumbers().map((page, index) =>
                typeof page === "number" ? (
                  <PaginationItem key={index}>
                    <PaginationLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        onPageChange(page);
                      }}
                      isActive={currentPage === page}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ) : (
                  <PaginationItem key={index}>
                    <span className="flex h-9 w-9 items-center justify-center">
                      {page}
                    </span>
                  </PaginationItem>
                ),
              )}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage < (meta?.pages || 1))
                      onPageChange(currentPage + 1);
                  }}
                  className={
                    currentPage === (meta?.pages || 1)
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}

        {meta && (
          <div className="text-sm text-gray-500 whitespace-nowrap">
            {t("projects.showing")} {(currentPage - 1) * (meta.limit || 12) + 1}
            -{Math.min(currentPage * (meta.limit || 12), meta.total)}{" "}
            {t("projects.of")} {meta.total} {t("projects.projects")}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectList;
